import {
  LocalLedgerBridgeResultSchema,
  type LocalLedgerBridgeResult,
} from '@carsfinders/contracts';

import { apiFetch } from '../../lib/api';
import {
  type AdminBid,
  type AuditEntry,
  type AuditResponse,
  type BidDetailResponse,
  type BidStatus,
  type CancelAllBidsResponse,
  type MinimumBidState,
  type RelayAction,
  type RelayClaimAction,
  type RelayQueueItem,
  type RelayQueueResponse,
  type SetMinimumBidResponse,
} from './types';

/**
 * Bids feature API — every call hits api-node directly (ported from the web RTK
 * Query `bidsApi` + the relay-desk routes). Endpoints consumed:
 * - GET   /bids?view=standard          → admin sees ALL bids (populated user).
 * - PATCH /bids/:id/status             → admin status change (Won / Not Won).
 * - PATCH /bids/:id/processed          → admin processed-on-auction toggle.
 * - POST  /bids/sync-statuses          → reconcile bid statuses, returns count.
 * - GET   /bids/:id                    → single bid + car + user (admin).
 * - GET   /admin/relay-queue           → captured|queued relay desk rows.
 * - POST  /admin/relay-queue/:id/*     → queue / confirm-relay / outcome / cancel / reject.
 * - POST  /admin/relay-queue/:id/{claim,release} → Phase 13 operator work lock.
 * - GET   /admin/audit?bidId=          → Phase 13 operator trail for one bid.
 * - GET   /admin/cars/:carId/minimum-bid     → lot floor + provenance.
 * - PATCH /admin/cars/:carId/minimum-bid     → set the lot floor (audited).
 * - POST  /admin/cars/:carId/cancel-all-bids → void every live bid (audited).
 * - POST  /admin/bids/:id/local-vehicle      → an admin's own win → local-ledger card.
 */

/** All bids, newest first (admin scope). */
export function fetchBids(): Promise<AdminBid[]> {
  return apiFetch<AdminBid[]>('bids', { searchParams: { view: 'standard' } });
}

/**
 * Change a bid's auction status (admin only).
 *
 * `forceReplace` is the second half of the winner-replacement handshake: with
 * it false (the default) api-node answers 409 `{requiresConfirmation, ...}`
 * when the lot already has a winner, and the caller must re-issue with true
 * after the operator confirms. Hardcoding false — as this client did before —
 * made replacing a winner impossible from the admin UI.
 */
export function updateBidStatus(
  id: string,
  status: BidStatus,
  forceReplace = false,
): Promise<unknown> {
  return apiFetch(`bids/${id}/status`, {
    method: 'PATCH',
    body: { status, forceReplace },
  });
}

/** Current minimum-bid floor of a lot plus why it is what it is (admin only). */
export function fetchMinimumBid(carId: string): Promise<MinimumBidState> {
  return apiFetch<MinimumBidState>(`admin/cars/${carId}/minimum-bid`);
}

/** Set the lot's minimum-bid floor; bids below it are demoted to Outbid. */
export function setMinimumBid(
  carId: string,
  minimumBidPrice: number,
): Promise<SetMinimumBidResponse> {
  return apiFetch<SetMinimumBidResponse>(`admin/cars/${carId}/minimum-bid`, {
    method: 'PATCH',
    body: { minimumBidPrice },
  });
}

/** Void every still-live bid on a lot in one audited transaction. */
export function cancelAllBidsForCar(carId: string): Promise<CancelAllBidsResponse> {
  return apiFetch<CancelAllBidsResponse>(`admin/cars/${carId}/cancel-all-bids`, {
    method: 'POST',
  });
}

/** Toggle the processed-on-auction flag of a bid (admin only). */
export function setBidProcessed(id: string, processedOnAuction: boolean): Promise<unknown> {
  return apiFetch(`bids/${id}/processed`, {
    method: 'PATCH',
    body: { processedOnAuction },
  });
}

/** Reconcile bid statuses across the system; returns how many were fixed. */
export function syncBidStatuses(): Promise<{ totalFixed?: number }> {
  return apiFetch<{ totalFixed?: number }>('bids/sync-statuses', { method: 'POST' });
}

/** One bid with its associated car and (admin-only) bidder detail. */
export function fetchBidDetail(id: string): Promise<BidDetailResponse> {
  return apiFetch<BidDetailResponse>(`bids/${id}`);
}

/** The relay desk queue (captured|queued bids, tenderCloseAt asc). */
export async function fetchRelayQueue(): Promise<RelayQueueItem[]> {
  const response = await apiFetch<RelayQueueResponse>('admin/relay-queue');
  return response.data ?? [];
}

/** Perform a relay action on a bid (queue / confirm-relay / outcome / cancel / reject). */
export function relayAction(
  id: string,
  action: RelayAction,
  body?: Record<string, unknown>,
): Promise<unknown> {
  return apiFetch(`admin/relay-queue/${id}/${action}`, {
    method: 'POST',
    body: body ?? {},
  });
}

/**
 * Phase 13 — take / hand back the operator claim on a relay row. `reason` is
 * required by the API only when releasing a COLLEAGUE's claim (an override).
 */
export function relayClaimAction(
  id: string,
  action: RelayClaimAction,
  body?: { reason?: string },
): Promise<unknown> {
  return apiFetch(`admin/relay-queue/${id}/${action}`, {
    method: 'POST',
    body: body ?? {},
  });
}

/** Phase 13 — the operator trail of one bid, newest first (GET /admin/audit?bidId=). */
export async function fetchBidAudit(bidId: string, limit = 100): Promise<AuditEntry[]> {
  const response = await apiFetch<AuditResponse>('admin/audit', {
    searchParams: { bidId, limit: String(limit) },
  });
  return response.data ?? [];
}

/**
 * «Додати до обліку» — an admin's own won bid becomes a card in the local
 * ledger (local.cars-finders.com), with no purchase rate yet. Idempotent: a
 * bid that already became a card answers that card (`existing: true`).
 * Parsed, not cast — the card carries coerced dates.
 */
export async function addBidToLedger(bidId: string): Promise<LocalLedgerBridgeResult> {
  const payload = await apiFetch<unknown>(`admin/bids/${encodeURIComponent(bidId)}/local-vehicle`, {
    method: 'POST',
    body: {},
  });
  return LocalLedgerBridgeResultSchema.parse(payload);
}
